import { FunctionComponent, KeyboardEvent, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import moment from 'moment'
import NoteService from '../../../shared/services/note/note.service'
import { INote, NoteKind, NoteParent } from '../../../shared/services/note/note.model'
import {
  NOTE_BODY_COUNTER_FROM,
  NOTE_BODY_MAX,
  NOTE_KIND_DEFAULT_DISCLOSE,
  NOTE_KIND_OPTIONS,
  getNoteKindLabel,
} from '../../../shared/constants/notes'
import { UserNotificationsContext } from '../../../context/user-notifications/user-notifications'
import { ReportPeriodContext } from '../../../context/report-periods/report-periods'
import { useSkeletonDelay } from '../../../hooks/use-skeleton-delay'
import { AvatarDisplay } from '../display-elements-components/avatar-display'
import { TableActionButtons } from '../miscellaneous-components/table-action-buttons'
import { BRAND_DISPLAY_FONT } from '../../pages/signup-login/auth/auth-branding'
import { formatGroupLabel } from '../../../shared/constants/ghg-category'

export interface INotesPanelSystemEntry {
  label: string
  text: string
}

export interface INotesPanelPeriodOption {
  id: string
  label: string
}

export interface INotesPanelGroupOption {
  id: string
  label: string
}

interface INotesPanelProps {
  relatedModelName: NoteParent
  relatedObjectId: string
  /**
   * Passed for emission and period parents: the period the note belongs to. Informational: the
   * server derives a note's period from its parent, so the panel never sends it.
   */
  reportPeriodId?: string
  /** Period notes on a group page: shows only that group's notes and stamps new ones with it. */
  groupId?: string
  /** The caller resolves role and scope (the same flags that gate evidence upload). */
  canWrite: boolean
  /** Read-only entries shown first, e.g. the emission's reason for exclusion. */
  systemEntries?: INotesPanelSystemEntry[]
  /** Location, supplier and vehicle notes only: the periods whose audit pack can include the note. */
  periodOptions?: INotesPanelPeriodOption[]
  /**
   * Period notes on the overview only: the groups a note can be linked to, shown as an optional
   * Group list in the note box. (On a group page, groupId links every note to that group instead.)
   */
  groupOptions?: INotesPanelGroupOption[]
  /**
   * Period notes only: switches the page to its Assumptions tab. When given, the composer tells the
   * user that a judgement the report should print is recorded as an assumption, not a note.
   */
  onAddAssumption?: () => void
}

// The rule this panel teaches: a note explains a record for the team and the auditor and is never
// printed in a report. The one thing a note can be included in is the audit pack's Notes sheet; the
// switch and the chip say exactly that. (The field is still called discloseInReports.)
const INCLUDE_LABEL = 'Include in audit pack'
const INCLUDED_CHIP = 'In audit pack'
const NOTES_ARE_NOT_IN_REPORTS = 'Notes are for your team and your auditor. They are never printed in your SECR or UK SRS reports.'

interface IComposerValues {
  body: string
  kind: NoteKind
  discloseInReports: boolean
  reportPeriodId: string
  /** '' means no group. Only used when the composer shows the Group list. */
  groupId: string
}

const mutedChipSx = { color: 'text.secondary', borderColor: 'lightGrey.main', flexShrink: 0 }
// Same accent as the primary "Selected calculations" card on the emission detail page
// (emission-detail-table.tsx paperAccent), so a note reads as a record card, not a chat bubble.
// borderRadius 1 is the theme shape radius (10px).
const noteCardSx = { bgcolor: '#f4f8ea', border: '1px solid', borderColor: 'primary.light', borderRadius: 1, px: 2, py: 1.5 }
const systemCardSx = { border: '1px solid', borderColor: 'lightGrey.main', borderRadius: 1, px: 2, py: 1.5 }

// The app's numbered group label for a note's group ("Scope 3.6: Business Travel"), the same text the
// Group list shows; the plain title if the scope did not come back.
const noteGroupLabel = (note: INote): string | null => {
  if (!note.groupId || !note.groupName) return null
  if (!note.groupScope) return note.groupName
  return formatGroupLabel({ id: note.groupId, title: note.groupName, scope: note.groupScope, scope3CategoryNumber: note.groupScope3CategoryNumber })
}

const initialsOf = (name: string | null): string => {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  return `${parts[0]?.charAt(0) ?? ''}${parts.length > 1 ? parts[parts.length - 1].charAt(0) : ''}`.toUpperCase() || '?'
}

/**
 * Composer for a new note, and for editing one in place. Enter saves, Shift+Enter breaks a line
 * (the CopilotInput behaviour). Changing the kind resets the audit pack switch to that kind's
 * default (on for Estimate and Exclusion, off for General); the author can still flip it.
 */
const NoteComposer: FunctionComponent<{
  initialValues: IComposerValues
  showPeriodPicker: boolean
  periodOptions: INotesPanelPeriodOption[]
  isSubmitting: boolean
  submitLabel: string
  onSubmit: (values: IComposerValues) => Promise<boolean>
  onCancel?: () => void
  autoFocus?: boolean
  /** Shows an optional Group list (period notes on the overview). */
  groupOptions?: INotesPanelGroupOption[]
  footer?: ReactNode
}> = ({ initialValues, showPeriodPicker, periodOptions, isSubmitting, submitLabel, onSubmit, onCancel, autoFocus = false, groupOptions, footer }) => {
  const [values, setValues] = useState<IComposerValues>(initialValues)
  const inputRef = useRef<HTMLTextAreaElement | null>(null)
  // Editing opens with the existing text: put the cursor after it, where a correction or an addition
  // usually goes, rather than before the first character.
  useEffect(() => {
    if (!autoFocus) return
    const input = inputRef.current
    if (!input) return
    const end = input.value.length
    input.setSelectionRange(end, end)
  }, [autoFocus])
  const trimmedLength = values.body.trim().length
  const tooLong = values.body.length > NOTE_BODY_MAX
  const needsPeriod = showPeriodPicker && values.discloseInReports && !values.reportPeriodId
  const canSubmit = trimmedLength > 0 && !tooLong && !needsPeriod && !isSubmitting

  const submit = async () => {
    if (!canSubmit) return
    const saved = await onSubmit(values)
    if (saved && !onCancel) {
      // A new note: clear the composer but keep the chosen kind and period for the next one.
      setValues((current) => ({ ...current, body: '' }))
    }
  }

  const keyDownHandler = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      submit()
    }
  }

  return (
    <Box sx={{ border: '1px solid', borderColor: 'lightGrey.main', borderRadius: '5px', p: 1.5 }}>
      <TextField
        fullWidth
        multiline
        minRows={2}
        maxRows={6}
        size='small'
        autoFocus={autoFocus}
        value={values.body}
        placeholder='Add a note for your team or for the auditor'
        onChange={(event) => setValues((current) => ({ ...current, body: event.target.value }))}
        onKeyDown={keyDownHandler}
        disabled={isSubmitting}
        error={tooLong}
        helperText={values.body.length > NOTE_BODY_COUNTER_FROM
          ? `${values.body.length.toLocaleString('en-GB')} of ${NOTE_BODY_MAX.toLocaleString('en-GB')} characters`
          : 'Enter to save, Shift+Enter for a new line'}
        inputRef={inputRef}
        inputProps={{ 'aria-label': 'Note' }}
      />
      <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }} gap={1.5} sx={{ mt: 1.5 }}>
        <FormControl size='small' sx={{ minWidth: 140 }}>
          <InputLabel id='note-kind-label'>Type</InputLabel>
          <Select
            labelId='note-kind-label'
            label='Type'
            value={values.kind}
            disabled={isSubmitting}
            onChange={(event) => {
              const kind = event.target.value as NoteKind
              setValues((current) => ({ ...current, kind, discloseInReports: NOTE_KIND_DEFAULT_DISCLOSE[kind] }))
            }}
          >
            {NOTE_KIND_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {groupOptions && (
          <FormControl size='small' sx={{ minWidth: 220 }}>
            <InputLabel id='note-group-label' shrink>Group (optional)</InputLabel>
            <Select
              labelId='note-group-label'
              label='Group (optional)'
              value={values.groupId}
              displayEmpty
              notched
              disabled={isSubmitting}
              onChange={(event) => setValues((current) => ({ ...current, groupId: event.target.value }))}
            >
              <MenuItem value=''>No group</MenuItem>
              {groupOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Box>
          <FormControlLabel
            sx={{ mr: 0 }}
            control={
              <Switch
                checked={values.discloseInReports}
                disabled={isSubmitting}
                onChange={(event) => setValues((current) => ({ ...current, discloseInReports: event.target.checked }))}
              />
            }
            label={<Typography variant='body2'>{INCLUDE_LABEL}</Typography>}
          />
        </Box>
        {showPeriodPicker && values.discloseInReports && (
          <FormControl size='small' sx={{ minWidth: 180 }} error={needsPeriod}>
            <InputLabel id='note-period-label'>Reporting period</InputLabel>
            <Select
              labelId='note-period-label'
              label='Reporting period'
              value={values.reportPeriodId}
              disabled={isSubmitting}
              onChange={(event) => setValues((current) => ({ ...current, reportPeriodId: event.target.value }))}
            >
              {periodOptions.map((option) => (
                <MenuItem key={option.id} value={option.id}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Box sx={{ flexGrow: 1 }} />
        <Stack direction='row' gap={1} justifyContent='flex-end'>
          {onCancel && (
            <Button variant='text' onClick={onCancel} disabled={isSubmitting}>
              <Typography>Cancel</Typography>
            </Button>
          )}
          <Button
            variant='contained'
            onClick={submit}
            disabled={!canSubmit}
            startIcon={isSubmitting ? <CircularProgress size={14} color='inherit' /> : undefined}
          >
            <Typography>{submitLabel}</Typography>
          </Button>
        </Stack>
      </Stack>
      {footer}
    </Box>
  )
}

/**
 * Notes on one record: a line saying what notes are for, the composer, read-only system entries, then
 * the notes newest first. A note never changes a figure and is never printed in a report. Notes
 * included in the audit pack reach its Notes sheet; the rest stay in the app.
 */
export const NotesPanel: FunctionComponent<INotesPanelProps> = ({
  relatedModelName,
  relatedObjectId,
  groupId,
  canWrite,
  systemEntries = [],
  periodOptions,
  groupOptions,
  onAddAssumption,
}) => {
  const noteService = useMemo(() => new NoteService(), [])
  const userNotificationsContext = useContext(UserNotificationsContext)
  const reportPeriodContext = useContext(ReportPeriodContext)
  const [notes, setNotes] = useState<INote[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const showSkeleton = useSkeletonDelay(isLoading)

  // Entity parents (location, supplier, vehicle) name a period only when the note is in the audit
  // pack, so the pack for that period knows to list it.
  const isEntityParent = relatedModelName === 'location' || relatedModelName === 'contactSupplier' || relatedModelName === 'vehicle'
  const options = useMemo(() => periodOptions ?? [], [periodOptions])
  const defaultPeriodId = useMemo(() => {
    const preferred = reportPeriodContext.lastSelectedOpenReportId
    if (preferred && options.some((option) => option.id === preferred)) return preferred
    return options[0]?.id ?? ''
  }, [reportPeriodContext.lastSelectedOpenReportId, options])

  useEffect(() => {
    let isActive = true
    setIsLoading(true)
    setErrorMessage('')
    setEditingId(null)
    noteService.getAll({ relatedModelName, relatedObjectId, groupId })
      .then((response) => {
        if (isActive) setNotes(response)
      })
      .catch(() => {
        if (isActive) setErrorMessage('Unable to load the notes. Please try again.')
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false)
          setHasLoaded(true)
        }
      })
    return () => {
      isActive = false
    }
  }, [noteService, relatedModelName, relatedObjectId, groupId])

  const failureSnackbar = (err: any, fallback: string) => {
    userNotificationsContext.openSnackbar({
      open: true,
      message: err?.response?.data?.message || fallback,
      backgroundColor: 'text.primary',
    })
  }

  const createHandler = async (values: IComposerValues): Promise<boolean> => {
    setIsSubmitting(true)
    try {
      const discloseInReports = values.discloseInReports
      const created = await noteService.create({
        relatedModelName,
        relatedObjectId,
        kind: values.kind,
        body: values.body,
        discloseInReports,
        reportPeriodId: isEntityParent && discloseInReports ? values.reportPeriodId || null : null,
        // On a group page every note is linked to that group; on the overview, to the group chosen.
        groupId: relatedModelName === 'reportPeriod' ? (groupId ?? (values.groupId || null)) : null,
      })
      setNotes((current) => [created, ...current])
      userNotificationsContext.openSnackbar({ open: true, message: 'Note added' })
      return true
    } catch (err: any) {
      failureSnackbar(err, 'The note could not be saved. Please try again.')
      return false
    } finally {
      setIsSubmitting(false)
    }
  }

  const updateHandler = async (note: INote, values: IComposerValues): Promise<boolean> => {
    setIsSubmitting(true)
    try {
      const updated = await noteService.update(note.id, {
        kind: values.kind,
        body: values.body,
        discloseInReports: values.discloseInReports,
        ...(isEntityParent && values.discloseInReports ? { reportPeriodId: values.reportPeriodId || null } : {}),
        ...(showGroupList ? { groupId: values.groupId || null } : {}),
      })
      setNotes((current) => current.map((n) => (n.id === note.id ? updated : n)))
      setEditingId(null)
      userNotificationsContext.openSnackbar({ open: true, message: 'Note updated' })
      return true
    } catch (err: any) {
      failureSnackbar(err, 'The note could not be updated. Please try again.')
      return false
    } finally {
      setIsSubmitting(false)
    }
  }

  const deleteHandler = (note: INote) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: note.discloseInReports
        ? 'Are you sure you want to delete this note? It will also be removed from the audit pack.'
        : 'Are you sure you want to delete this note?',
      buttonCopy: 'Delete note',
      cancelButtonCopy: 'Cancel',
    }).then((confirmed) => {
      if (!confirmed) return
      noteService.remove(note.id)
        .then(() => {
          setNotes((current) => current.filter((n) => n.id !== note.id))
          userNotificationsContext.openSnackbar({ open: true, message: 'Note deleted' })
        })
        .catch((err) => failureSnackbar(err, 'The note could not be deleted. Please try again.'))
    })
  }

  const newNoteValues: IComposerValues = {
    body: '',
    kind: 'general',
    discloseInReports: NOTE_KIND_DEFAULT_DISCLOSE.general,
    reportPeriodId: defaultPeriodId,
    groupId: '',
  }

  const periodLabel = (id: string | null) => options.find((option) => option.id === id)?.label

  // The overview's period notes offer an optional Group list; a group page fixes the group instead.
  const showGroupList = relatedModelName === 'reportPeriod' && !groupId && Boolean(groupOptions)
  // When editing a note linked to a group that is no longer offered (a Scope 3 group switched off
  // since), keep that group in the list so the edit does not silently drop the link.
  const groupOptionsFor = (note: INote | null): INotesPanelGroupOption[] => {
    const list = groupOptions ?? []
    const label = note ? noteGroupLabel(note) : null
    if (note?.groupId && label && !list.some((option) => option.id === note.groupId)) {
      return [...list, { id: note.groupId, label }]
    }
    return list
  }

  const includedChipLabel = (note: INote): string => {
    const label = INCLUDED_CHIP
    const period = isEntityParent ? periodLabel(note.reportPeriodId) : undefined
    return period ? `${label}: ${period}` : label
  }

  // Period notes: something the report should say is an assumption, not a note.
  const assumptionPointer = relatedModelName === 'reportPeriod' && onAddAssumption ? (
    <Typography variant='caption' component='p' sx={{ color: 'text.secondary', mt: 1 }}>
      Need this in your SECR or UK SRS report?{' '}
      <Link component='button' type='button' variant='caption' underline='hover' onClick={onAddAssumption} sx={{ verticalAlign: 'baseline' }}>
        Add it as an assumption
      </Link>
      {' '}instead.
    </Typography>
  ) : null

  return (
    <Stack gap={1.5} sx={{ maxWidth: '880px' }}>
      <Typography variant='body2' sx={{ color: 'text.secondary' }}>
        {NOTES_ARE_NOT_IN_REPORTS}
      </Typography>

      {/* The note box sits at the top, above the notes (newest first), so adding one never means
          scrolling past a long list, and a saved note appears straight under the box. */}
      {canWrite && hasLoaded && !errorMessage && editingId === null && (
        <NoteComposer
          key={`new-${relatedObjectId}-${defaultPeriodId}`}
          initialValues={newNoteValues}
          showPeriodPicker={isEntityParent}
          periodOptions={options}
          isSubmitting={isSubmitting}
          submitLabel='Save note'
          onSubmit={createHandler}
          groupOptions={showGroupList ? groupOptionsFor(null) : undefined}
          footer={assumptionPointer}
        />
      )}

      {systemEntries.map((entry) => (
        <Box key={entry.label} sx={systemCardSx}>
          <Stack direction='row' alignItems='center' gap={1} sx={{ mb: 0.5 }}>
            <Chip size='small' variant='outlined' label='System' sx={mutedChipSx} />
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>{entry.label}</Typography>
          </Stack>
          <Typography variant='body2' sx={{ color: 'text.primary', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{entry.text}</Typography>
        </Box>
      ))}

      {errorMessage && <Alert severity='error'>{errorMessage}</Alert>}

      {isLoading && showSkeleton && (
        <>
          <Skeleton variant='rounded' height={88} />
          <Skeleton variant='rounded' height={88} />
        </>
      )}

      {hasLoaded && !isLoading && !errorMessage && notes.length === 0 && (
        <Typography variant='body2' sx={{ color: 'text.secondary', py: 1 }}>
          No notes yet.
        </Typography>
      )}

      {!isLoading && notes.map((note) => (
        editingId === note.id ? (
          <NoteComposer
            key={note.id}
            autoFocus
            initialValues={{
              body: note.body,
              kind: note.kind,
              discloseInReports: note.discloseInReports,
              reportPeriodId: note.reportPeriodId ?? defaultPeriodId,
              groupId: note.groupId ?? '',
            }}
            showPeriodPicker={isEntityParent}
            periodOptions={options}
            isSubmitting={isSubmitting}
            submitLabel='Save'
            onSubmit={(values) => updateHandler(note, values)}
            onCancel={() => setEditingId(null)}
            groupOptions={showGroupList ? groupOptionsFor(note) : undefined}
          />
        ) : (
          <Box key={note.id} sx={noteCardSx}>
            <Stack direction='row' alignItems='flex-start' gap={1.5}>
              <AvatarDisplay
                color={note.createdByUserAvatarColor || '#e7e7e7'}
                width='30px'
                height='30px'
                padding={0}
                fontSize='0.8rem'
                name={initialsOf(note.createdByUserName)}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction='row' alignItems='center' flexWrap='wrap' columnGap={1} rowGap={0.5}>
                  <Typography variant='body2' sx={{ fontFamily: BRAND_DISPLAY_FONT, color: 'text.primary' }}>
                    {note.createdByUserName || 'Former user'}
                  </Typography>
                  <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                    {moment(note.createdAt).format('DD/MM/YYYY HH:mm')}
                  </Typography>
                  {noteGroupLabel(note) && !groupId && (
                    <Chip size='small' variant='outlined' label={noteGroupLabel(note)} sx={mutedChipSx} />
                  )}
                  <Chip size='small' variant='outlined' label={getNoteKindLabel(note.kind)} sx={mutedChipSx} />
                  {note.discloseInReports && (
                    <Chip
                      size='small'
                      variant='outlined'
                      label={includedChipLabel(note)}
                      sx={mutedChipSx}
                    />
                  )}
                  {note.editedAt && (
                    <Typography variant='caption' sx={{ color: 'text.secondary' }}>edited</Typography>
                  )}
                </Stack>
                <Typography variant='body2' sx={{ color: 'text.primary', whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.75 }}>
                  {note.body}
                </Typography>
              </Box>
              {note.canModify && (
                <Box sx={{ mt: -0.75, mr: -0.75 }}>
                  <TableActionButtons
                    id={note.id}
                    includeEdit={true}
                    includeDelete={true}
                    disableButton={isSubmitting || editingId !== null}
                    onEdit={() => setEditingId(note.id)}
                    onDelete={() => deleteHandler(note)}
                  />
                </Box>
              )}
            </Stack>
          </Box>
        )
      ))}

    </Stack>
  )
}
